import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const github_com_falqondev_reportdev_datalake_backend_service_ApplicationRole =
  z
    .object({
      aproID: z.string(),
      description: z.string(),
      name: z.string(),
      roles: z.array(z.string()),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_AuditLogView = z
  .object({
    action: z.string(),
    auloID: z.number(),
    createdAt: z.string(),
    createdBy: z.string(),
    resourceMetadata: z.unknown(),
    resourceName: z.string(),
    userInfo: z
      .object({
        avatar: z.string().nullable(),
        email: z.string().nullable(),
        name: z.string().nullable(),
      })
      .partial()
      .passthrough(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ChartBlockView = z
  .object({
    cachedData: z.unknown(),
    chartDefinition: z.unknown(),
    chblID: z.string(),
    code: z.string(),
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    lang: z.string(),
    name: z.string(),
    projID: z.string(),
    thumbnail: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();
const chartBlockCreate_Body = z
  .object({
    cachedData: z.unknown(),
    chartDefinition: z.unknown(),
    code: z.string(),
    description: z.string(),
    lang: z.string(),
    name: z.string(),
    projID: z.string(),
    thumbnail: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ConnectionSecretView =
  z
    .object({
      connectString: z.string(),
      coseID: z.string(),
      createdAt: z.string(),
      credentials: z
        .object({
          accountName: z.string().nullish(),
          chain: z.string().nullish(),
          clientCertificatePath: z.string().nullish(),
          clientID: z.string().nullish(),
          clientSecret: z.string().nullish(),
          database: z.string().nullish(),
          password: z.string().nullish(),
          provider: z.string().nullish(),
          tenantID: z.string().nullish(),
          type: z.string(),
          user: z.string().nullish(),
        })
        .passthrough(),
      deletedAt: z.string().nullish(),
      host: z.string().nullish(),
      name: z.string(),
      port: z.number().nullish(),
      ssh: z
        .object({
          enabled: z.boolean(),
          host: z.string().nullish(),
          passphrase: z.string().nullish(),
          port: z.number().nullish(),
          privateKey: z.string().nullish(),
          user: z.string().nullish(),
        })
        .passthrough()
        .nullish(),
      ssl: z
        .object({
          ca: z.string().nullish(),
          cert: z.string().nullish(),
          enabled: z.boolean(),
          key: z.string().nullish(),
          mode: z.string().nullish(),
        })
        .passthrough()
        .nullish(),
    })
    .passthrough();
const connectionSecretCreate_Body = z
  .object({
    connectString: z.string().nullish(),
    credentials: z
      .object({
        accountName: z.string().nullish(),
        chain: z.string().nullish(),
        clientCertificatePath: z.string().nullish(),
        clientID: z.string().nullish(),
        clientSecret: z.string().nullish(),
        database: z.string().nullish(),
        password: z.string().nullish(),
        provider: z.string().nullish(),
        tenantID: z.string().nullish(),
        type: z.string(),
        user: z.string().nullish(),
      })
      .passthrough(),
    host: z.string().nullish(),
    name: z.string(),
    port: z.number().nullish(),
    ssh: z
      .object({
        enabled: z.boolean(),
        host: z.string().nullish(),
        passphrase: z.string().nullish(),
        port: z.number().nullish(),
        privateKey: z.string().nullish(),
        user: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    ssl: z
      .object({
        ca: z.string().nullish(),
        cert: z.string().nullish(),
        enabled: z.boolean(),
        key: z.string().nullish(),
        mode: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TableData = z
  .object({
    fqn: z.string(),
    objectType: z.string(),
    schemaName: z.string(),
    tableName: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_DashboardView = z
  .object({
    createdAt: z.string(),
    dashID: z.string(),
    data: z.unknown(),
    deletedAt: z.string().nullish(),
    name: z.string(),
    projID: z.string(),
  })
  .passthrough();
const dashboardCreate_Body = z
  .object({ data: z.unknown(), name: z.string(), projID: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_DocumentationView =
  z
    .object({
      content: z.string(),
      createdAt: z.string(),
      docuID: z.number(),
      tags: z.array(z.string()),
      title: z.string(),
      videoURL: z.string(),
    })
    .passthrough();
const documentationCreate_Body = z
  .object({
    content: z.string(),
    tags: z.array(z.string()).nullish(),
    title: z.string(),
    videoURL: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_Document = z
  .object({
    createdAt: z.string(),
    data: z.unknown(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    docuID: z.string(),
    doteID: z.string(),
    formSchema: z.unknown(),
    permissionMode: z.string(),
    printSchema: z.unknown(),
    title: z.string(),
    uiSchema: z.unknown(),
    updatedAt: z.string(),
  })
  .passthrough();
const documentCreate_Body = z
  .object({
    data: z.unknown(),
    description: z.string(),
    doteID: z.string(),
    formSchema: z.unknown(),
    permissionMode: z.string(),
    printSchema: z.unknown(),
    title: z.string(),
    uiSchema: z.unknown(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ErrorReportView =
  z
    .object({
      createdAt: z.string(),
      erreID: z.number(),
      filterRegex: z.string(),
      name: z.string(),
      notifiedUsers: z.array(z.string()),
      updatedAt: z.string(),
    })
    .passthrough();
const errorReportCreate_Body = z
  .object({
    filterRegex: z.string(),
    name: z.string(),
    notifiedUsers: z.array(z.string()),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_EdgeView = z
  .object({
    description: z.string().nullish(),
    doc_id: z.string().nullish(),
    fromEntity: z
      .object({
        fqn: z.string(),
        fullyQualifiedNameHash: z.string().nullish(),
        id: z.string(),
        type: z.string(),
      })
      .passthrough(),
    pipeline: z
      .object({
        description: z.string().nullish(),
        display_name: z.string(),
        id: z.string(),
        name: z.string(),
      })
      .passthrough(),
    source: z.string().nullish(),
    sqlQuery: z.string().nullish(),
    toEntity: z
      .object({
        fqn: z.string(),
        fullyQualifiedNameHash: z.string().nullish(),
        id: z.string(),
        type: z.string(),
      })
      .passthrough(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_EntityView = z
  .object({
    database: z
      .object({
        description: z.string(),
        displayName: z.string(),
        fullyQualifiedName: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.string(),
      })
      .passthrough()
      .nullish(),
    databaseSchema: z
      .object({
        description: z.string(),
        displayName: z.string(),
        fullyQualifiedName: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.string(),
      })
      .passthrough()
      .nullish(),
    description: z.string().nullish(),
    displayName: z.string().nullish(),
    entityType: z.string(),
    fullyQualifiedName: z.string(),
    id: z.string(),
    lineage: z
      .array(github_com_falqondev_reportdev_datalake_backend_service_EdgeView)
      .nullish(),
    name: z.string(),
    service: z
      .object({
        description: z.string(),
        displayName: z.string(),
        fullyQualifiedName: z.string(),
        id: z.string(),
        name: z.string(),
        type: z.string(),
      })
      .passthrough()
      .nullish(),
    serviceType: z.string().nullish(),
    tableType: z.string().nullish(),
  })
  .passthrough();
const oneTimeLoginCreate_Body = z
  .object({ userID: z.string(), validUntil: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode = z
  .object({
    code: z.string(),
    database: z.string(),
    executionOrder: z.number(),
    lang: z.string(),
    resourceID: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask = z
  .object({
    createdAt: z.string(),
    payload: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
    ),
    plseID: z.string(),
    response: z.array(z.object({}).partial().passthrough()).nullish(),
    runID: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PlaygroundSessionView =
  z
    .object({
      createdAt: z.string(),
      expiresAt: z.string(),
      plseID: z.string(),
      resourceID: z.string(),
      resourceType: z.string(),
      tasks: z.array(
        github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask
      ),
    })
    .passthrough();
const playgroundSessionCreate_Body = z
  .object({ resourceID: z.string(), resourceType: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ColumnInfo = z
  .object({
    defaultVal: z.string(),
    name: z.string(),
    nullable: z.boolean(),
    type: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TableInfo = z
  .object({
    columns: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_ColumnInfo
    ),
    name: z.string(),
    rows: z.number(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_SchemaInfo = z
  .object({
    name: z.string(),
    tables: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_TableInfo
    ),
  })
  .passthrough();
const playgroundRunCreate_Body = z
  .object({
    databases: z.array(z.string()),
    executionGroup: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
    ),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ProjectView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    name: z.string(),
    projID: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid =
  z.object({ dashID: z.string(), name: z.string() }).passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_ReportView = z
  .object({
    createdAt: z.string(),
    dashboards: z
      .array(
        github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid
      )
      .nullish(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    enabled: z.boolean(),
    frequency: z.string(),
    message: z.string(),
    name: z.string(),
    projID: z.string(),
    reatID: z.string(),
    recipients: z.array(z.string()),
    repoID: z.string(),
    reportFailureEmail: z.array(z.string()),
    sendMethod: z.string(),
    subject: z.string(),
    templateRejectionReason: z.string().nullish(),
    templateSid: z.string().nullish(),
    templateStatus: z.string().nullish(),
    useTextAttachments: z.boolean(),
  })
  .passthrough();
const reportCreate_Body = z
  .object({
    dashboards: z.array(z.string()),
    description: z.string(),
    enabled: z.boolean(),
    frequency: z.string(),
    message: z.string(),
    name: z.string(),
    projID: z.string(),
    recipients: z.array(z.string()),
    reportFailureEmail: z.array(z.string()),
    sendMethod: z.string(),
    subject: z.string(),
    useTextAttachments: z.boolean(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_PendingTerm = z
  .object({ slug: z.string(), termID: z.number(), title: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_Term = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    slug: z.string(),
    termID: z.number(),
    title: z.string(),
    type: z.string(),
  })
  .passthrough();
const termCreate_Body = z
  .object({
    description: z.string(),
    slug: z.string(),
    title: z.string(),
    type: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TestSuitData = z
  .object({ name: z.string(), tesuID: z.number() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TestView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    fullQualifiedName: z.string().nullish(),
    lastRun: z.string().nullish(),
    name: z.string(),
    suites: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
    ),
    testID: z.string(),
  })
  .passthrough();
const testCreate_Body = z
  .object({
    description: z.string(),
    name: z.string(),
    tesuIDs: z.array(z.number()),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_TestSuiteView = z
  .object({
    createdAt: z.string(),
    deletedAt: z.string().nullish(),
    description: z.string(),
    frequency: z.string(),
    name: z.string(),
    schedulingEnabled: z.boolean(),
    testIDs: z.array(z.string()),
    tesuID: z.number(),
  })
  .passthrough();
const testSuiteCreate_Body = z
  .object({
    description: z.string(),
    frequency: z.string(),
    name: z.string(),
    schedulingEnabled: z.boolean(),
    testIDs: z.array(z.string()),
  })
  .passthrough();
const testCodeBlockCreate_Body = z
  .object({ code: z.string(), fullQualifiedName: z.string(), lang: z.string() })
  .passthrough();
const userRoleCreate_Body = z
  .object({ aproIDs: z.array(z.string()), userID: z.string() })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_UserInformation =
  z
    .object({
      applID: z.string(),
      authenticationProvider: z.string(),
      avatar: z.string().nullish(),
      createdAt: z.string(),
      deletedAt: z.string().nullish(),
      email: z.string().nullish(),
      info: z.object({}).partial().passthrough(),
      name: z.string(),
      pushTokens: z.array(z.string()).nullish(),
      roles: z.array(z.string()),
      userID: z.string(),
    })
    .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_InstitutionUserCreateFormByUser =
  z
    .object({
      admittedAt: z.string().nullish(),
      buunID: z.number().nullish(),
      code: z.string().nullish(),
      instID: z.string(),
      operationalCode: z.string().nullish(),
      phone: z.string(),
      sex: z.string(),
    })
    .passthrough();
const userCreate_Body = z
  .object({
    applID: z.string(),
    avatar: z.string().nullish(),
    email: z.string(),
    institutions: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_InstitutionUserCreateFormByUser
    ),
    name: z.string(),
    password: z.string().nullish(),
  })
  .passthrough();
const userUpdate_Body = z
  .object({
    avatar: z.string().nullable(),
    email: z.string().nullable(),
    name: z.string().nullable(),
  })
  .partial()
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_RootInformation =
  z.object({ frequency: z.string(), workID: z.string() }).passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CodeBlock = z
  .object({
    coblID: z.string(),
    code: z.string(),
    createdAt: z.string(),
    database: z.string(),
    description: z.string(),
    executionOrder: z.number(),
    hash: z.string(),
    lang: z.string(),
    name: z.string(),
    parentID: z.string().nullish(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_WorkflowView = z
  .object({
    createdAt: z.string(),
    databases: z.array(z.string()),
    deletedAt: z.string().nullish(),
    dependsWorkIDs: z.array(z.string()),
    deployedAt: z.string().nullish(),
    description: z.string(),
    frequency: z.string(),
    hash: z.string(),
    name: z.string(),
    parentNames: z.array(z.string()),
    roots: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_RootInformation
    ),
    schedulingEnabled: z.boolean(),
    scripts: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
    ),
    wcegID: z.string(),
    wodeID: z.number().nullish(),
    workID: z.string(),
    woveID: z.number(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_CodeBlocksForm = z
  .object({
    coblID: z.string().nullish(),
    code: z.string(),
    database: z.string(),
    description: z.string(),
    executionOrder: z.number(),
    lang: z.string(),
    name: z.string(),
    parentID: z.string().nullish(),
  })
  .passthrough();
const workflowCreate_Body = z
  .object({
    databases: z.array(z.string()),
    dependsWorkIDs: z.array(z.string()).nullish(),
    deploy: z.boolean().nullish(),
    description: z.string().nullish(),
    frequency: z.string(),
    name: z.string(),
    schedulingEnabled: z.boolean(),
    scripts: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_CodeBlocksForm
    ),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_WorkflowParent = z
  .object({
    forbiddenParents: z.array(z.string()),
    name: z.string(),
    roots: z.array(
      github_com_falqondev_reportdev_datalake_backend_service_RootInformation
    ),
    workID: z.string(),
  })
  .passthrough();
const github_com_falqondev_reportdev_datalake_backend_service_WorkflowVersionHistory =
  z
    .object({
      createdAt: z.string(),
      hash: z.string(),
      wodeID: z.number().nullish(),
      workID: z.string(),
      woveID: z.number(),
    })
    .passthrough();

export const schemas = {
  github_com_falqondev_reportdev_datalake_backend_service_ApplicationRole,
  github_com_falqondev_reportdev_datalake_backend_service_AuditLogView,
  github_com_falqondev_reportdev_datalake_backend_service_ChartBlockView,
  chartBlockCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ConnectionSecretView,
  connectionSecretCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_TableData,
  github_com_falqondev_reportdev_datalake_backend_service_DashboardView,
  dashboardCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_DocumentationView,
  documentationCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_Document,
  documentCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ErrorReportView,
  errorReportCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_EdgeView,
  github_com_falqondev_reportdev_datalake_backend_service_EntityView,
  oneTimeLoginCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode,
  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask,
  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundSessionView,
  playgroundSessionCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ColumnInfo,
  github_com_falqondev_reportdev_datalake_backend_service_TableInfo,
  github_com_falqondev_reportdev_datalake_backend_service_SchemaInfo,
  playgroundRunCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_ProjectView,
  github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid,
  github_com_falqondev_reportdev_datalake_backend_service_ReportView,
  reportCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_PendingTerm,
  github_com_falqondev_reportdev_datalake_backend_service_Term,
  termCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData,
  github_com_falqondev_reportdev_datalake_backend_service_TestView,
  testCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_TestSuiteView,
  testSuiteCreate_Body,
  testCodeBlockCreate_Body,
  userRoleCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_UserInformation,
  github_com_falqondev_reportdev_datalake_backend_service_InstitutionUserCreateFormByUser,
  userCreate_Body,
  userUpdate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_RootInformation,
  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock,
  github_com_falqondev_reportdev_datalake_backend_service_WorkflowView,
  github_com_falqondev_reportdev_datalake_backend_service_CodeBlocksForm,
  workflowCreate_Body,
  github_com_falqondev_reportdev_datalake_backend_service_WorkflowParent,
  github_com_falqondev_reportdev_datalake_backend_service_WorkflowVersionHistory,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/api/application-roles",
    alias: "applicationRoleList",
    description: `Lists all roles available`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "aproID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ApplicationRole
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/audit-logs",
    alias: "auditLogsList",
    description: `Lists all logs`,
    requestFormat: "json",
    parameters: [
      {
        name: "resource",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "action",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userName",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "userEmail",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_AuditLogView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/audit-logs/download",
    alias: "auditLogsDownload",
    description: `Get logs export file`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "action",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "resource",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userEmail",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userName",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.string(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/auth-media",
    alias: "mediaAuth",
    description: `Authenticate media server access`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z
          .object({ mediaURL: z.string().nullable() })
          .partial()
          .passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.object({ jwt: z.string() }).passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/chart-block",
    alias: "chartBlockList",
    description: `Lists all chart_blocks`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "chblID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ChartBlockView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/chart-block",
    alias: "chartBlockCreate",
    description: `Create one chart_block`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: chartBlockCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/chart-block/:chblID",
    alias: "chartBlockDelete",
    description: `Delete one chart_block by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "chblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/chart-block/:chblID",
    alias: "chartBlockGet",
    description: `Get one chart_block`,
    requestFormat: "json",
    parameters: [
      {
        name: "chblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/chart-block/:chblID",
    alias: "chartBlockUpdate",
    description: `Update chart_block`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: chartBlockCreate_Body,
      },
      {
        name: "chblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                cachedData: z.unknown(),
                chartDefinition: z.unknown(),
                chblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                lang: z.string(),
                name: z.string(),
                projID: z.string(),
                thumbnail: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/code-blocks/:coblID",
    alias: "codeBlocksGet",
    description: `Get one code_blocks`,
    requestFormat: "json",
    parameters: [
      {
        name: "coblID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                coblID: z.string(),
                code: z.string(),
                createdAt: z.string(),
                database: z.string(),
                description: z.string(),
                executionOrder: z.number(),
                hash: z.string(),
                lang: z.string(),
                name: z.string(),
                parentID: z.string().nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connect/playground",
    alias: "playgroundConnect",
    description: `Connect in playground websocket`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.string(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret",
    alias: "connectionSecretList",
    description: `Lists all connection_secrets`,
    requestFormat: "json",
    parameters: [
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "names",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "coseID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "credentialType",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ConnectionSecretView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/connection-secret",
    alias: "connectionSecretCreate",
    description: `Create one connection_secret`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: connectionSecretCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/connection-secret/:coseID",
    alias: "connectionSecretDelete",
    description: `Delete one connection_secret by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "coseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret/:coseID",
    alias: "connectionSecretGet",
    description: `Get one connection_secret`,
    requestFormat: "json",
    parameters: [
      {
        name: "coseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/connection-secret/:coseID",
    alias: "connectionSecretUpdate",
    description: `Update connection_secret`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: connectionSecretCreate_Body,
      },
      {
        name: "coseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                connectString: z.string(),
                coseID: z.string(),
                createdAt: z.string(),
                credentials: z
                  .object({
                    accountName: z.string().nullish(),
                    chain: z.string().nullish(),
                    clientCertificatePath: z.string().nullish(),
                    clientID: z.string().nullish(),
                    clientSecret: z.string().nullish(),
                    database: z.string().nullish(),
                    password: z.string().nullish(),
                    provider: z.string().nullish(),
                    tenantID: z.string().nullish(),
                    type: z.string(),
                    user: z.string().nullish(),
                  })
                  .passthrough(),
                deletedAt: z.string().nullish(),
                host: z.string().nullish(),
                name: z.string(),
                port: z.number().nullish(),
                ssh: z
                  .object({
                    enabled: z.boolean(),
                    host: z.string().nullish(),
                    passphrase: z.string().nullish(),
                    port: z.number().nullish(),
                    privateKey: z.string().nullish(),
                    user: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
                ssl: z
                  .object({
                    ca: z.string().nullish(),
                    cert: z.string().nullish(),
                    enabled: z.boolean(),
                    key: z.string().nullish(),
                    mode: z.string().nullish(),
                  })
                  .passthrough()
                  .nullish(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret/:name/verification",
    alias: "connectionSecretNameVerification",
    description: `Validate a duckdb secret names`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "ignoreID",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                alternativeName: z.string(),
                name: z.string(),
                valid: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/connection-secret/default/tables-data",
    alias: "connectionSecretDefaultTablesDataList",
    description: `List data from all tables in the default database connection`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "fullQualifiedNames",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "keyword",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_TableData
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/dashboard",
    alias: "dashboardList",
    description: `Lists all dashboards`,
    requestFormat: "json",
    parameters: [
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "dashID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_DashboardView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/dashboard",
    alias: "dashboardCreate",
    description: `Create one dashboard`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: dashboardCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/dashboard/:dashID",
    alias: "dashboardDelete",
    description: `Delete one dashboard by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "dashID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/dashboard/:dashID",
    alias: "dashboardGet",
    description: `Get one dashboard`,
    requestFormat: "json",
    parameters: [
      {
        name: "dashID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/dashboard/:dashID",
    alias: "dashboardUpdate",
    description: `Update dashboard`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: dashboardCreate_Body,
      },
      {
        name: "dashID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashID: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documentations",
    alias: "documentationList",
    description: `Lists all documentations`,
    requestFormat: "json",
    parameters: [
      {
        name: "title",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "docuIDs",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "tags",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_DocumentationView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/documentations",
    alias: "documentationCreate",
    description: `Create one documentation`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentationCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/documentations/:docuID",
    alias: "documentationDelete",
    description: `Delete one documentation by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documentations/:docuID",
    alias: "documentationGet",
    description: `Get one documentation`,
    requestFormat: "json",
    parameters: [
      {
        name: "docuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/documentations/:docuID",
    alias: "documentationUpdate",
    description: `Update documentation`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentationCreate_Body,
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                content: z.string(),
                createdAt: z.string(),
                docuID: z.number(),
                tags: z.array(z.string()),
                title: z.string(),
                videoURL: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documents",
    alias: "documentList",
    description: `Lists all document`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "Limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "Offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_Document
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/documents",
    alias: "documentCreate",
    description: `Create one document`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/documents/:docuID",
    alias: "documentDelete",
    description: `Delete one document by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/documents/:docuID",
    alias: "documentGet",
    description: `Get one document`,
    requestFormat: "json",
    parameters: [
      {
        name: "docuID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/documents/:docuID",
    alias: "documentUpdate",
    description: `Update document`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: documentCreate_Body,
      },
      {
        name: "docuID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                data: z.unknown(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                docuID: z.string(),
                doteID: z.string(),
                formSchema: z.unknown(),
                permissionMode: z.string(),
                printSchema: z.unknown(),
                title: z.string(),
                uiSchema: z.unknown(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/error-report",
    alias: "errorReportList",
    description: `Lists all error_reports`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "erreID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ErrorReportView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/error-report",
    alias: "errorReportCreate",
    description: `Create one error_report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: errorReportCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                notifiedUsers: z.array(z.string()),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/error-report/:erreID",
    alias: "errorReportDelete",
    description: `Delete one error_report by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "erreID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/error-report/:erreID",
    alias: "errorReportGet",
    description: `Get one error_report`,
    requestFormat: "json",
    parameters: [
      {
        name: "erreID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                notifiedUsers: z.array(z.string()),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/error-report/:erreID",
    alias: "errorReportUpdate",
    description: `Update error_report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: errorReportCreate_Body,
      },
      {
        name: "erreID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                erreID: z.number(),
                filterRegex: z.string(),
                name: z.string(),
                notifiedUsers: z.array(z.string()),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/file/upload",
    alias: "fileUpload",
    description: `Save one file on the server`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z.object({ filename: z.string() }).passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/lineage",
    alias: "lineageGet",
    description: `Get one lineage`,
    requestFormat: "json",
    parameters: [
      {
        name: "fqn",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                edges: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_EdgeView
                ),
                entity: z
                  .object({
                    database: z
                      .object({
                        description: z.string(),
                        displayName: z.string(),
                        fullyQualifiedName: z.string(),
                        id: z.string(),
                        name: z.string(),
                        type: z.string(),
                      })
                      .passthrough()
                      .nullish(),
                    databaseSchema: z
                      .object({
                        description: z.string(),
                        displayName: z.string(),
                        fullyQualifiedName: z.string(),
                        id: z.string(),
                        name: z.string(),
                        type: z.string(),
                      })
                      .passthrough()
                      .nullish(),
                    description: z.string().nullish(),
                    displayName: z.string().nullish(),
                    entityType: z.string(),
                    fullyQualifiedName: z.string(),
                    id: z.string(),
                    lineage: z
                      .array(
                        github_com_falqondev_reportdev_datalake_backend_service_EdgeView
                      )
                      .nullish(),
                    name: z.string(),
                    service: z
                      .object({
                        description: z.string(),
                        displayName: z.string(),
                        fullyQualifiedName: z.string(),
                        id: z.string(),
                        name: z.string(),
                        type: z.string(),
                      })
                      .passthrough()
                      .nullish(),
                    serviceType: z.string().nullish(),
                    tableType: z.string().nullish(),
                  })
                  .passthrough(),
                nodes: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_EntityView
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/one-time-login",
    alias: "oneTimeLoginCreate",
    description: `Create new oneTimeLogin`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: oneTimeLoginCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                otloID: z.number(),
                token: z.string(),
                userID: z.string(),
                validUntil: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/playground/:plseID/cancel-task/:runID",
    alias: "playgroundTaskCancel",
    description: `Cancel playground task`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "runID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                payload: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
                ),
                plseID: z.string(),
                response: z
                  .array(z.object({}).partial().passthrough())
                  .nullish(),
                runID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/:plseID/database/:dbname",
    alias: "playgroundDatabaseGet",
    description: `Get database information`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "dbname",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                dbName: z.string(),
                plseID: z.string(),
                schemas: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_SchemaInfo
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/:plseID/pending-task",
    alias: "playgroundTaskGetPending",
    description: `Get playground pending tasks`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                payload: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
                ),
                plseID: z.string(),
                response: z
                  .array(z.object({}).partial().passthrough())
                  .nullish(),
                runID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/playground/:plseID/run-task",
    alias: "playgroundRunCreate",
    description: `Create one playground_session`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: playgroundRunCreate_Body,
      },
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                payload: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundCode
                ),
                plseID: z.string(),
                response: z
                  .array(z.object({}).partial().passthrough())
                  .nullish(),
                runID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/session",
    alias: "playgroundSessionList",
    description: `Lists all playground_sessions`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "resourceID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "resourceType",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_PlaygroundSessionView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/playground/session",
    alias: "playgroundSessionCreate",
    description: `Create one playground_session`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: playgroundSessionCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                expiresAt: z.string(),
                plseID: z.string(),
                resourceID: z.string(),
                resourceType: z.string(),
                tasks: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/playground/session/:plseID",
    alias: "playgroundSessionGet",
    description: `Get one playground_session`,
    requestFormat: "json",
    parameters: [
      {
        name: "plseID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                expiresAt: z.string(),
                plseID: z.string(),
                resourceID: z.string(),
                resourceType: z.string(),
                tasks: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PlaygroundTask
                ),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/project",
    alias: "projectList",
    description: `Lists all projects`,
    requestFormat: "json",
    parameters: [
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ProjectView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/project",
    alias: "projectCreate",
    description: `Create one project`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ name: z.string() }).passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/project/:projID",
    alias: "projectDelete",
    description: `Delete one project by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "projID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/project/:projID",
    alias: "projectGet",
    description: `Get one project`,
    requestFormat: "json",
    parameters: [
      {
        name: "projID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/project/:projID",
    alias: "projectUpdate",
    description: `Update project`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ name: z.string() }).passthrough(),
      },
      {
        name: "projID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                name: z.string(),
                projID: z.string(),
                updatedAt: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/report",
    alias: "reportList",
    description: `Lists all reports`,
    requestFormat: "json",
    parameters: [
      {
        name: "schedulingEnabled",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "projID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "includeRemoved",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "reatID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "repoID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_ReportView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/report",
    alias: "reportCreate",
    description: `Create one report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: reportCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashboards: z
                  .array(
                    github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid
                  )
                  .nullish(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/report/:repoID",
    alias: "reportDelete",
    description: `Delete one report by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "repoID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashboards: z
                  .array(
                    github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid
                  )
                  .nullish(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/report/:repoID",
    alias: "reportGet",
    description: `Get one report`,
    requestFormat: "json",
    parameters: [
      {
        name: "repoID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashboards: z
                  .array(
                    github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid
                  )
                  .nullish(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/report/:repoID",
    alias: "reportUpdate",
    description: `Update report`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: reportCreate_Body,
      },
      {
        name: "repoID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                dashboards: z
                  .array(
                    github_com_falqondev_reportdev_datalake_backend_service_ReportDashboardGrid
                  )
                  .nullish(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                enabled: z.boolean(),
                frequency: z.string(),
                message: z.string(),
                name: z.string(),
                projID: z.string(),
                reatID: z.string(),
                recipients: z.array(z.string()),
                repoID: z.string(),
                reportFailureEmail: z.array(z.string()),
                sendMethod: z.string(),
                subject: z.string(),
                templateRejectionReason: z.string().nullish(),
                templateSid: z.string().nullish(),
                templateStatus: z.string().nullish(),
                useTextAttachments: z.boolean(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/term/accept",
    alias: "termAccept",
    description: `Accept a term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({ termID: z.number() }).passthrough(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                accepted: z.boolean(),
                termID: z.number(),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/term/pending",
    alias: "termGetPending",
    description: `get all pending terms`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                terms: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_PendingTerm
                ),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/terms",
    alias: "termList",
    description: `Lists all terms`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "slug",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "title",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "active",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_Term
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/terms",
    alias: "termCreate",
    description: `Create one term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: termCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/terms/:termID",
    alias: "termDelete",
    description: `Delete one term by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "termID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/terms/:termID",
    alias: "termGet",
    description: `Get one term`,
    requestFormat: "json",
    parameters: [
      {
        name: "termID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/terms/:termID",
    alias: "termUpdate",
    description: `Update term`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: termCreate_Body,
      },
      {
        name: "termID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                slug: z.string(),
                termID: z.number(),
                title: z.string(),
                type: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test",
    alias: "testList",
    description: `Lists all tests`,
    requestFormat: "json",
    parameters: [
      {
        name: "testID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "fullQualifiedName",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_TestView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/test",
    alias: "testCreate",
    description: `Create one test`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test-suite",
    alias: "testSuiteList",
    description: `Lists all test_suites`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "schedulingEnabled",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "tesuID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_TestSuiteView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/test-suite",
    alias: "testSuiteCreate",
    description: `Create one test_suite`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testSuiteCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/test-suite/:tesuID",
    alias: "testSuiteDelete",
    description: `Delete one test_suite by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "tesuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test-suite/:tesuID",
    alias: "testSuiteGet",
    description: `Get one test_suite`,
    requestFormat: "json",
    parameters: [
      {
        name: "tesuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/test-suite/:tesuID",
    alias: "testSuiteUpdate",
    description: `Update test_suite`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testSuiteCreate_Body,
      },
      {
        name: "tesuID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                name: z.string(),
                schedulingEnabled: z.boolean(),
                testIDs: z.array(z.string()),
                tesuID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/test/:testID",
    alias: "testDelete",
    description: `Delete one test by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test/:testID",
    alias: "testGet",
    description: `Get one test`,
    requestFormat: "json",
    parameters: [
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/test/:testID",
    alias: "testUpdate",
    description: `Update test`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testCreate_Body,
      },
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                description: z.string(),
                fullQualifiedName: z.string().nullish(),
                lastRun: z.string().nullish(),
                name: z.string(),
                suites: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_TestSuitData
                ),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/test/:testID/code-block",
    alias: "testCodeBlockGet",
    description: `Get one test code block`,
    requestFormat: "json",
    parameters: [
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                fullQualifiedName: z.string(),
                hash: z.string(),
                lang: z.string(),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/test/:testID/code-block",
    alias: "testCodeBlockCreate",
    description: `Create one test code block`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: testCodeBlockCreate_Body,
      },
      {
        name: "testID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                code: z.string(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                fullQualifiedName: z.string(),
                hash: z.string(),
                lang: z.string(),
                testID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/user-roles",
    alias: "userRoleCreate",
    description: `Create new user userRole`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userRoleCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ roles: z.array(z.string()), userID: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/user-roles",
    alias: "userRoleUpdate",
    description: `Update one userRole`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userRoleCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({ roles: z.array(z.string()), userID: z.string() })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/users",
    alias: "userList",
    description: `Lists all users`,
    requestFormat: "json",
    parameters: [
      {
        name: "aproID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "email",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "applID",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "keywords",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "deleted",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "instID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "userID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_UserInformation
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/users",
    alias: "userCreate",
    description: `Create new user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/users/:userID",
    alias: "userDelete",
    description: `Delete one user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/users/:userID",
    alias: "userGet",
    description: `Get one user by userID`,
    requestFormat: "json",
    parameters: [
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "patch",
    path: "/api/users/:userID",
    alias: "userUpdate",
    description: `Update one user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: userUpdate_Body,
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/users/:userID/enable",
    alias: "userEnable",
    description: `Enable a deleted user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "userID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                applID: z.string(),
                authenticationProvider: z.string(),
                avatar: z.string().nullish(),
                createdAt: z.string(),
                deletedAt: z.string().nullish(),
                email: z.string().nullish(),
                info: z.object({}).partial().passthrough(),
                name: z.string(),
                pushTokens: z.array(z.string()).nullish(),
                roles: z.array(z.string()),
                userID: z.string(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow",
    alias: "workflowList",
    description: `Lists all workflows`,
    requestFormat: "json",
    parameters: [
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "workID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "schedulingEnabled",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "createdAt[lte]",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "woveID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "validParentsFor",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "createdAt[gte]",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_WorkflowView
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/workflow",
    alias: "workflowCreate",
    description: `Create one workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: workflowCreate_Body,
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "delete",
    path: "/api/workflow/:workID",
    alias: "workflowDelete",
    description: `Delete one workflow by id`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z
          .object({ forceAndUnlinkDependencies: z.boolean().nullable() })
          .partial()
          .passthrough(),
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow/:workID",
    alias: "workflowGet",
    description: `Get one workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "woveID",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "put",
    path: "/api/workflow/:workID",
    alias: "workflowUpdate",
    description: `Update workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: workflowCreate_Body,
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "post",
    path: "/api/workflow/:workID/deploy/:woveID",
    alias: "workflowDeploy",
    description: `Deploy Workflow version`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Request data`,
        type: "Body",
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "woveID",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            item: z
              .object({
                createdAt: z.string(),
                databases: z.array(z.string()),
                deletedAt: z.string().nullish(),
                dependsWorkIDs: z.array(z.string()),
                deployedAt: z.string().nullish(),
                description: z.string(),
                frequency: z.string(),
                hash: z.string(),
                name: z.string(),
                parentNames: z.array(z.string()),
                roots: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_RootInformation
                ),
                schedulingEnabled: z.boolean(),
                scripts: z.array(
                  github_com_falqondev_reportdev_datalake_backend_service_CodeBlock
                ),
                wcegID: z.string(),
                wodeID: z.number().nullish(),
                workID: z.string(),
                woveID: z.number(),
              })
              .passthrough(),
            kind: z.string(),
            lang: z.string().nullish(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow/:workID/valid-parents",
    alias: "workflowValidParentList",
    description: `List all valid parent workflows`,
    requestFormat: "json",
    parameters: [
      {
        name: "workID",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "invalidWorkIDs",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().nullish(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_WorkflowParent
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
  {
    method: "get",
    path: "/api/workflow/:workID/version-history",
    alias: "workflowVersionHistoryList",
    description: `Get workflow version history`,
    requestFormat: "json",
    parameters: [
      {
        name: "workID",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().nullish(),
      },
    ],
    response: z
      .object({
        context: z.string().nullish(),
        data: z
          .object({
            currentItemCount: z.number(),
            items: z.array(
              github_com_falqondev_reportdev_datalake_backend_service_WorkflowVersionHistory
            ),
            itemsPerPage: z.number(),
            kind: z.string(),
            lang: z.string().nullish(),
            pageIndex: z.number(),
            startIndex: z.number(),
            totalItems: z.number(),
            totalPages: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
